/*
 * os 项目本地请求器（Vue3 + Vue CLI / Webpack5）
 * - 支持本地开发同源代理：/__proxy/{serviceKey}
 * - 支持按服务细粒度覆盖域名（uc/cms/os/ai/pay/fsf）
 * - 拦截器/弹窗提示内聚在本文件
 */

import axios from "axios";
import User from "./user";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";

function normalizeUrl(value) {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("/")) return trimmed.replace(/\/+$/, "") || "/";
    return trimmed.replace(/\/+$/, "");
}

// 注意：Vue CLI 只会在编译期替换 `process.env.XYZ` 这类静态访问。
// 不要用 `process.env[key]` 动态读取，否则会在浏览器运行时出现 `process is not defined`。
const ENV = Object.freeze({
    nodeEnv: process.env.NODE_ENV,
    proxyEnable: process.env.VUE_APP_PROXY_ENABLE,
    proxyPrefix: process.env.VUE_APP_PROXY_PREFIX,

    // common 域名（统一入口）
    api: process.env.VUE_APP_COMMON_API,
    fsf: process.env.VUE_APP_FSF_API,
    pay: process.env.VUE_APP_PAY_API,

    uc: process.env.VUE_APP_UC_API,
    cms: process.env.VUE_APP_CMS_API,
    os: process.env.VUE_APP_OS_API,
    ai: process.env.VUE_APP_AI_API,
});

function localProxyEnabled(options) {
    if (ENV.nodeEnv !== "development") return false;
    if (options && options.proxy === false) return false;
    if (options && options.proxy === true) return true;
    // 显式指定 domain 时，一般是为了走特定的直连或 devServer 代理（例如 domain: "/" + /api/manage）。
    // 这种情况下默认禁用 /__proxy/{serviceKey}，避免覆盖调用侧意图；如需强制仍走 /__proxy，可传 options.proxy=true。
    if (options && options.domain) return false;
    const raw = String(ENV.proxyEnable || "").toLowerCase();
    return raw === "1" || raw === "true" || raw === "yes" || raw === "on";
}

function localProxyBase(serviceKey) {
    const prefix = ENV.proxyPrefix || "/__proxy";
    return `${prefix}/${serviceKey}`;
}

function resolveDomainByServiceKey(serviceKey, options = {}) {
    if (options.domain) return normalizeUrl(options.domain);

    const common = normalizeUrl(ENV.api);
    const specific = normalizeUrl(ENV[serviceKey]);
    return specific || common;
}

function joinBaseAndPath(base, path) {
    const b = normalizeUrl(base);
    if (!path) return b;
    const p = `/${String(path).replace(/^\/+/, "")}`;
    if (!b) return p;
    if (b === "/") return p;
    return `${b}${p}`;
}

function buildBaseURL({ serviceKey, basePath = "", options }) {
    const cleanedBasePath = basePath ? `/${String(basePath).replace(/^\/+/, "")}` : "";
    if (localProxyEnabled(options) && serviceKey) return `${localProxyBase(serviceKey)}${cleanedBasePath}`;
    const domain = resolveDomainByServiceKey(serviceKey, options);
    return joinBaseAndPath(domain, cleanedBasePath);
}

function resolveServiceUrl(serviceKey, path, options = {}) {
    const normalizedPath = path ? `/${String(path).replace(/^\/+/, "")}` : "";
    if (localProxyEnabled(options)) return `${localProxyBase(serviceKey)}${normalizedPath}`;
    const domain = resolveDomainByServiceKey(serviceKey, options);
    return joinBaseAndPath(domain, normalizedPath);
}

function buildAuthHeaders() {
    const token = User?.getToken?.() || "";
    return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

function loadPop(msg, popType = "message") {
    const message = msg || "网络请求异常";
    switch (popType) {
        case "message":
            ElMessage({ message, type: "error" });
            break;
        case "alert":
            ElMessageBox.alert(message, "错误");
            break;
        case "notify":
            ElNotification({ title: "错误", message, duration: 3000 });
            break;
        default:
            ElMessage({ message, type: "error" });
            break;
    }
}

function loadDefaultRequestErrorPop(err, popType = "message") {
    const status = err?.response?.status ?? "";
    const statusText = err?.response?.statusText ?? "Request Error";
    loadPop(`[${status}]${statusText}`, popType);
}

function installStandardInterceptors(target, options) {
    const popType = (options && options.popType) || "message";
    target.interceptors.response.use(
        (response) => {
            if (response?.data?.code) {
                if (!options || !options.mute) {
                    response.data.msg && loadPop(`[${response.data.code}]${response.data.msg}`, popType);
                }
                return Promise.reject(response);
            }
            return response;
        },
        (err) => {
            if (!options || !options.mute) {
                if (err?.response?.data?.msg) {
                    loadPop(err.response.data.msg, popType);
                } else {
                    loadDefaultRequestErrorPop(err, popType);
                }
            }
            return Promise.reject(err);
        }
    );
}

function installDedupPrefixInterceptor(target, prefix) {
    const normalizedPrefix = prefix ? `/${String(prefix).replace(/^\/+/, "").replace(/\/+$/, "")}` : "";
    if (!normalizedPrefix) return;

    target.interceptors.request.use((config) => {
        const url = config?.url;
        if (!url || typeof url !== "string") return config;
        if (/^https?:\/\//i.test(url)) return config;

        const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
        if (normalizedUrl === normalizedPrefix || normalizedUrl.startsWith(`${normalizedPrefix}/`)) {
            const stripped = normalizedUrl.slice(normalizedPrefix.length);
            config.url = stripped ? stripped : "/";
        }
        return config;
    });
}

function createAxiosInstance(options = {}, axiosConfig = {}, { serviceKey, basePath = "", dedupePrefix = "" } = {}) {
    const config = {
        withCredentials: true,
        headers: Object.assign({}, buildAuthHeaders(), options.headers || {}),
        baseURL: buildBaseURL({ serviceKey, basePath, options }),
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    const ins = axios.create(Object.assign({}, axiosConfig, config));
    if (dedupePrefix) installDedupPrefixInterceptor(ins, dedupePrefix);
    if (options.interceptor !== false) installStandardInterceptors(ins, options);
    return ins;
}

// ===== 默认 common 请求器 =====
function $http(options = {}, axiosConfig = {}) {
    return createAxiosInstance(options, axiosConfig, { serviceKey: "api" });
}

// ===== fsf（期权） =====
function $fsf(options = {}, axiosConfig = {}) {
    const basePath = "/api/fsf";
    return createAxiosInstance(options, axiosConfig, { serviceKey: "fsf", basePath, dedupePrefix: basePath });
}

// ===== pay（资产）=====
// os 项目里历史上默认加 /api/pay 前缀（对应后端路由），这里保持兼容；若你在调用侧已写 /api/pay，可不重复
function $pay(options = {}, axiosConfig = {}) {
    const basePath = "/api/pay";
    return createAxiosInstance(options, axiosConfig, { serviceKey: "pay", basePath, dedupePrefix: basePath });
}

// ===== common 下的快捷封装（允许按服务覆盖域名） =====
function $uc(options = {}, axiosConfig = {}) {
    const basePath = "/api/uc";
    return createAxiosInstance(options, axiosConfig, { serviceKey: "uc", basePath, dedupePrefix: basePath });
}

function $cms(options = {}, axiosConfig = {}) {
    const basePath = "/api/cms";
    return createAxiosInstance(options, axiosConfig, { serviceKey: "cms", basePath, dedupePrefix: basePath });
}

function $os(options = {}, axiosConfig = {}) {
    const basePath = "/api/os";
    return createAxiosInstance(options, axiosConfig, { serviceKey: "os", basePath, dedupePrefix: basePath });
}

function $ai(options = {}, axiosConfig = {}) {
    const basePath = "/api/ai";
    return createAxiosInstance(options, axiosConfig, { serviceKey: "ai", basePath, dedupePrefix: basePath });
}

export {
    axios,
    resolveServiceUrl,
    installStandardInterceptors,
    $http,
    $fsf,
    $pay,
    $uc,
    $cms,
    $os,
    $ai,
};
